import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getAdminOverview } from '../../lib/api';
import type { AdminOverviewResponse } from '../../lib/types';
import {
  AdminBadge,
  AdminEmptyState,
  AdminPanel,
  AdminReadingCard,
  AdminStatCard,
  formatEstimatedLlmCost,
  formatSpreadLabel,
  formatTokenCount,
  shortId,
} from './AdminUi';

export default function AdminOverviewPage() {
  const [data, setData] = useState<AdminOverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let disposed = false;

    const load = async () => {
      setLoading(true);
      try {
        const response = await getAdminOverview();
        if (!disposed) {
          setData(response);
          setErrorMessage(null);
        }
      } catch (error) {
        if (!disposed) {
          const apiError = error as Error;
          setErrorMessage(apiError.message || '운영 개요를 불러오지 못했습니다.');
        }
      } finally {
        if (!disposed) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      disposed = true;
    };
  }, []);

  if (loading) {
    return (
      <AdminPanel title="운영 개요" subtitle="현재 관리자 지표를 불러오는 중입니다.">
        <p className="text-sm text-[#d8bfa1]/75">대시보드 데이터를 불러오고 있습니다...</p>
      </AdminPanel>
    );
  }

  if (errorMessage || !data) {
    return (
      <AdminPanel title="운영 개요" subtitle="대시보드 상태">
        <AdminEmptyState
          title="운영 개요를 불러올 수 없습니다"
          body={errorMessage || '대시보드 데이터를 가져오지 못했습니다.'}
        />
      </AdminPanel>
    );
  }

  const totalSource = data.source_metrics.live + data.source_metrics.fallback;
  const fallbackRate = totalSource > 0 ? Math.round((data.source_metrics.fallback / totalSource) * 100) : 0;
  const liveRate = totalSource > 0 ? 100 - fallbackRate : 0;

  return (
    <div className="space-y-6">
      <AdminPanel
        title="운영 개요"
        subtitle="실응답 비율, 대체 응답 비율, 현재 이슈를 한눈에 확인합니다."
        actions={<AdminBadge label={`프롬프트 ${data.prompt_version}`} tone="accent" />}
      >
        <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-6">
          <AdminStatCard label="오늘 생성된 리딩" value={data.kpis.readings_created_today} />
          <AdminStatCard label="오늘 결제 완료" value={data.kpis.payments_completed_today} />
          <AdminStatCard label="오늘 생성 실패" value={data.kpis.generation_failed_today} />
          <AdminStatCard label="실응답 비율" value={`${liveRate}%`} caption={`${data.source_metrics.live}건`} />
          <AdminStatCard label="대체 응답 비율" value={`${fallbackRate}%`} caption={`${data.source_metrics.fallback}건`} />
          <AdminStatCard label="주요 스프레드" value={data.spread_metrics.three_card} caption="쓰리카드 사용 수" />
        </div>
      </AdminPanel>

      <AdminPanel
        title="LLM 토큰·비용"
        subtitle="generationMeta.usage가 저장된 live 리딩 기준의 추정 비용입니다."
      >
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          <AdminStatCard label="집계 리딩" value={data.token_cost_metrics.count} />
          <AdminStatCard label="총 추정 비용" value={formatEstimatedLlmCost(data.token_cost_metrics.totalEstimatedCostUsd)} />
          <AdminStatCard label="평균 비용" value={formatEstimatedLlmCost(data.token_cost_metrics.averageEstimatedCostUsd)} />
          <AdminStatCard label="평균 토큰" value={formatTokenCount(data.token_cost_metrics.averageTotalTokens)} />
          <AdminStatCard label="평균 추론 토큰" value={formatTokenCount(data.token_cost_metrics.averageReasoningTokens)} />
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-3">
          {data.token_cost_metrics.bySpread.map((item) => (
            <div key={item.spread} className="border border-[#8a6a4b]/20 bg-black/10 p-4">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-medium text-[#f4e2cb]">{formatSpreadLabel(item.spread)}</p>
                <p className="text-xs uppercase tracking-[0.16em] text-[#c9a092]/80">{item.count}건</p>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3 text-sm text-[#d8bfa1]/72">
                <div>
                  <p className="text-xs uppercase tracking-[0.16em] text-[#c9a092]/70">평균 비용</p>
                  <p className="mt-1 text-[#f4e2cb]">{formatEstimatedLlmCost(item.averageEstimatedCostUsd)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.16em] text-[#c9a092]/70">총 비용</p>
                  <p className="mt-1 text-[#f4e2cb]">{formatEstimatedLlmCost(item.totalEstimatedCostUsd)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.16em] text-[#c9a092]/70">평균 토큰</p>
                  <p className="mt-1 text-[#f4e2cb]">{formatTokenCount(item.averageTotalTokens)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.16em] text-[#c9a092]/70">평균 출력</p>
                  <p className="mt-1 text-[#f4e2cb]">{formatTokenCount(item.averageCompletionTokens)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </AdminPanel>

      <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
        <AdminPanel title="현재 이슈" subtitle="결제 후 생성 실패, 생성 지연, 결과 누락 리딩">
          <div className="space-y-3">
            {data.issues.length === 0 ? (
              <AdminEmptyState title="활성 이슈가 없습니다" body="실패했거나 멈춘 리딩이 발견되지 않았습니다." />
            ) : (
              data.issues.map((issue) => (
                <article key={`${issue.id}-${issue.issue_type}`} className="border border-[#8a6a4b]/25 bg-black/10 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <AdminBadge label={issue.issue_label} tone={issue.issue_type === 'generation_failed' ? 'danger' : 'warning'} />
                        <AdminBadge label={shortId(issue.id)} tone="muted" />
                      </div>
                      <p className="mt-3 text-sm font-medium text-[#f4e2cb]">
                        {issue.question || `리딩 ${shortId(issue.id)}`}
                      </p>
                      <p className="mt-2 text-sm leading-6 text-[#d8bfa1]/72">{issue.issue_note}</p>
                    </div>
                    <Link
                      to={`/admin/readings/${issue.id}`}
                      className="inline-flex items-center gap-2 border border-[#8a6a4b]/45 bg-[#241913] px-3 py-2 text-sm text-[#f3dfc5] hover:bg-[#2d2019]"
                    >
                      열기
                    </Link>
                  </div>
                </article>
              ))
            )}
          </div>
        </AdminPanel>

        <div className="space-y-6">
          <AdminPanel title="스프레드 사용량" subtitle="선택된 카드 수를 기준으로 추정한 현재 사용량입니다.">
            <div className="grid gap-3">
              <AdminStatCard label="원카드" value={data.spread_metrics.one_card} />
              <AdminStatCard label="쓰리카드" value={data.spread_metrics.three_card} />
              <AdminStatCard label="켈틱 크로스" value={data.spread_metrics.celtic_cross} />
            </div>
          </AdminPanel>

          <AdminPanel title="주제별 응답 출처" subtitle="주제별 실응답과 대체 응답 비중입니다.">
            <div className="space-y-3">
              {data.topic_source_metrics.map((item) => {
                const total = item.live + item.fallback;
                const ratio = total > 0 ? Math.round((item.fallback / total) * 100) : 0;
                return (
                  <div key={item.topic} className="border border-[#8a6a4b]/20 bg-black/10 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-medium text-[#f4e2cb]">{item.topic}</p>
                      <p className="text-xs uppercase tracking-[0.16em] text-[#c9a092]/80">대체 {ratio}%</p>
                    </div>
                    <p className="mt-2 text-sm text-[#d8bfa1]/70">실응답 {item.live}건 · 대체 응답 {item.fallback}건</p>
                  </div>
                );
              })}
            </div>
          </AdminPanel>
        </div>
      </div>

      <AdminPanel title="최근 리딩" subtitle="최근 생성되었거나 결제된 리딩 목록입니다.">
        <div className="space-y-3">
          {data.recent_readings.length === 0 ? (
            <AdminEmptyState title="아직 리딩이 없습니다" body="사용자가 리딩을 생성하면 이 목록에 표시됩니다." />
          ) : (
            data.recent_readings.map((reading) => (
              <AdminReadingCard
                key={reading.id}
                reading={reading}
                to={`/admin/readings/${reading.id}`}
              />
            ))
          )}
        </div>
      </AdminPanel>

      <AdminPanel title="스프레드별 응답 출처" subtitle="플로우 단계별 대체 응답 증가 여부를 빠르게 봅니다.">
        <div className="grid gap-4 md:grid-cols-3">
          {data.spread_source_metrics.map((item) => (
            <div key={item.spread} className="border border-[#8a6a4b]/20 bg-black/10 p-4">
              <p className="text-sm font-medium text-[#f4e2cb]">{formatSpreadLabel(item.spread)}</p>
              <p className="mt-2 text-sm text-[#d8bfa1]/72">실응답 {item.live}건</p>
              <p className="text-sm text-[#d8bfa1]/72">대체 응답 {item.fallback}건</p>
            </div>
          ))}
        </div>
      </AdminPanel>
    </div>
  );
}
